import React, { useEffect, useMemo, useState } from 'react';

import { useUIContext } from 'lib/context/UIContext/UIContext';
import { cn } from 'lib/helpers/utility';

import useBreadcrumb from 'components/shared/Breadcrumb/useBreadcrumb';
import Modal from 'components/shared/Modal/Modal';

import ModalFooter from './components/ModalFooter';
import ModalHeader from './components/ModalHeader';
import TabsTableContent from './components/TabsTableContent';

const TabsTableModal = ({
  tabs,
  prefix,
  initialStep = 0,
  initialValues = {},
  onSubmit = () => {},
  disableClick,
  isLoading,
  handleClose,
  headerActions,
  headerClassName,
  bodyClassName,
  insideBodyClassName,
  hasBackButton,
  trimSteps,
  ...rest
}) => {
  const breadcrumb = useBreadcrumb(tabs, initialStep);
  const tab = breadcrumb.current;

  const { device } = useUIContext();
  const isDesktop = device === 'desktop' || device === 'laptop';

  const [selected, setSelected] = useState({});
  const [details, setDetails] = useState(null);
  const [modal, setModal] = useState(null);
  const [modalValue, setModalValue] = useState(initialValues);
  const [skippedTabs, setSkippedTabs] = useState([]);
  const [fetchedData, setFetchedData] = useState(null);

  const resetSelections = () => {
    setSelected((old) => ({ ...old, [tab.name]: [] }));
  };

  const handleCloseWithReset = () => {
    try {
      setSelected({});
      setDetails(null);
      setModal(null);
      setModalValue({});
      setSkippedTabs([]);
      breadcrumb.setSteps(tabs);
      breadcrumb.setStep(initialStep);
    } finally {
      handleClose?.();
    }
  };

  const selectedData = useMemo(() => {
    return Object.keys(selected).reduce((acc, tabName) => {
      acc[tabName] = selected[tabName]?.map((row) => row.data) || [];
      return acc;
    }, {});
  }, [selected]);

  const alwaysShowDetails = tab?.details?.alwaysShow && isDesktop;
  const showDetails = details || alwaysShowDetails;

  useEffect(() => {
    if (tab?.skipStep && !skippedTabs.includes(tab?.name)) {
      breadcrumb.goNext();
    }
    if (!skippedTabs.includes(tab?.name)) {
      setSkippedTabs((prev) => [...prev, tab?.name]);
    }
  }, [tab]);

  const lockNext = tab?.disableContinue
    ? typeof tab?.disableContinue === 'function'
      ? tab?.disableContinue(selected)
      : tab?.disableContinue
    : false;

  return (
    <Modal
      width={isDesktop && showDetails ? 'full' : 'medium'}
      handleClose={handleCloseWithReset}
      bodyClassName={cn('p-0', bodyClassName)}
      shouldCloseOnEsc={!modal}
      footer={
        !isLoading && (
          <ModalFooter
            breadcrumb={breadcrumb}
            resetSelections={resetSelections}
            tab={tab}
            setDetails={setDetails}
            selectedData={selectedData}
            lockNext={lockNext}
            setModal={setModal}
            initialStep={initialStep}
            handleClose={handleCloseWithReset}
          />
        )
      }
      headerClassName={cn(
        'bg-white border-b border-neutral-200 border-solid border-t-0 border-x-0 px-4 py-3',
        headerClassName
      )}
      customHeader={
        <ModalHeader
          setDetails={setDetails}
          selectedData={selectedData}
          breadcrumb={breadcrumb}
          tab={tab}
          lockNext={lockNext}
          disableClick={disableClick}
          headerActions={headerActions}
          hasBackButton={hasBackButton}
          trimSteps={trimSteps}
        />
      }
      {...rest}>
      <TabsTableContent
        tab={tab}
        isLoading={isLoading}
        modal={modal}
        setModal={setModal}
        modalValue={modalValue}
        setModalValue={setModalValue}
        selectedData={selectedData}
        onSubmit={onSubmit}
        alwaysShowDetails={alwaysShowDetails}
        showDetails={showDetails}
        details={details}
        setDetails={setDetails}
        prefix={prefix}
        setSelected={setSelected}
        setFetchedData={setFetchedData}
        selected={selected}
        fetchedData={fetchedData}
        insideBodyClassName={insideBodyClassName}
        breadcrumb={breadcrumb}
      />
    </Modal>
  );
};

export default TabsTableModal;
